import { PATHS } from "../constants/paths";

/**
 * Client-side session handling for the admin app.
 *
 * The session is a bearer JWT in localStorage. The server is the only authority on whether it is
 * still good, but the client can cheaply (a) refuse a token whose `exp` has passed and (b) react
 * when the API says the token is no longer accepted.
 *
 * Deliberately has no imports from `dataProvider/*`: the transports import this module to report
 * 401/403 responses, and the server probe lives in `dataProvider/sessionCheck.js`.
 */

export const TOKEN_STORAGE_KEY = "jwtToken";

/** The token was rejected for good (401, or a failed server probe): drop it and show login. */
export const AUTH_EXPIRED_EVENT = "auth:expired";

/** A 403 came back: could be "not allowed to do this" or "session is dead"; ask the server. */
export const AUTH_RECHECK_EVENT = "auth:recheck";

export const getStoredToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

export const clearStoredToken = () => {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    // Storage unavailable: nothing to clear.
  }
};

/** Claims of a JWT, or null when the value is not a decodable JWT. Does not verify the signature. */
export const decodeJwtPayload = (token) => {
  if (typeof token !== "string") return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(window.atob(padded));
    return payload && typeof payload === "object" ? payload : null;
  } catch (_) {
    return null;
  }
};

/** True when the token decodes and its `exp` (seconds since epoch) is still in the future. */
export const isTokenUsable = (token, now = Date.now()) => {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === "number" && exp * 1000 > now;
};

/** The stored token if it is still usable; an unusable one is removed and null is returned. */
export const readUsableToken = () => {
  const token = getStoredToken();
  if (!token) return null;
  if (isTokenUsable(token)) return token;
  clearStoredToken();
  return null;
};

/** Drops the token and tells the app to show the login page. */
export const expireSession = () => {
  clearStoredToken();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

export const requestSessionRecheck = () => {
  window.dispatchEvent(new Event(AUTH_RECHECK_EVENT));
};

/**
 * Called by every API transport with the HTTP status of each response.
 *
 * 401 ends the session. 403 is ambiguous (the backend answers 403 both to an anonymous caller and
 * to a signed-in user who lacks a role), so it only asks the app to re-check the session.
 * Ignored when no token is stored, so a failed login attempt is not reported as an expiry.
 */
export const handleAuthStatus = (status) => {
  if (status !== 401 && status !== 403) return;
  if (!getStoredToken()) return;
  if (status === 401) expireSession();
  else requestSessionRecheck();
};

const subscribe = (eventName, handler) => {
  window.addEventListener(eventName, handler);
  return () => window.removeEventListener(eventName, handler);
};

export const onAuthExpired = (handler) =>
  subscribe(AUTH_EXPIRED_EVENT, handler);

export const onAuthRecheck = (handler) =>
  subscribe(AUTH_RECHECK_EVENT, handler);

/**
 * Where to go after a successful login: the page the user was sent away from, if it is an
 * in-app path, otherwise `fallback`.
 */
export const resolvePostLoginPath = (from, fallback = PATHS.TEAMS) => {
  const pathname = from?.pathname;
  if (
    typeof pathname !== "string" ||
    !pathname.startsWith("/") ||
    pathname.startsWith("//") ||
    pathname === "/" ||
    pathname === PATHS.LOGIN
  ) {
    return fallback;
  }
  return `${pathname}${from.search || ""}${from.hash || ""}`;
};
